import { Box, Card, styled, useTheme, Button, TextField, CircularProgress, Typography } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { useEffect, useState, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MembersTable from "page-sections/Chapter-attendance/ChapterMembersTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { toast } from "react-hot-toast";
import { usePageTitle } from "contexts/PageTitleContext";


const DatePickerWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    "& .react-datepicker-wrapper": {
        width: "130px"
    },
    "& .react-datepicker__input-container input": {
        padding: "8px 12px",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        width: "100%",
        fontSize: "0.875rem",
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:hover': {
            borderColor: theme.palette.primary.main
        },
        '&:focus': {
            borderColor: theme.palette.primary.main,
            outline: 'none',
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`
        }
    },
    "& .react-datepicker": {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        fontFamily: theme.typography.fontFamily,
        "& .react-datepicker__header": {
            backgroundColor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            "& .react-datepicker__current-month": {
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                fontWeight: theme.typography.fontWeightMedium
            },
            "& .react-datepicker__day-name": {
                color: theme.palette.text.secondary
            }
        },
        "& .react-datepicker__day": {
            color: theme.palette.text.primary,
            "&:hover": {
                backgroundColor: theme.palette.action.hover
            },
            "&.react-datepicker__day--selected": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                    backgroundColor: theme.palette.primary.dark
                }
            },
            "&.react-datepicker__day--keyboard-selected": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText
            },
            "&.react-datepicker__day--disabled": {
                color: theme.palette.text.disabled
            }
        },
        "& .react-datepicker__triangle": {
            display: "none"
        }
    }
}));

const ChapterAttendanceMembers = () => {
    const { setPageTitle } = usePageTitle();
    useEffect(() => {
        setPageTitle('Chapter Attendance Management');
    }, [setPageTitle]);
    
    const theme = useTheme();
    const [members, setMembers] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [meetingDetails, setMeetingDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [mom, setMom] = useState('');
    const [startTime, setStartTime] = useState('10:00');
    const [endTime, setEndTime] = useState('12:00');
    const [chapterMeetingId, setChapterMeetingId] = useState(null);

    const formatDateToAPI = (date) => {
        if (!date) return null;
        const isoString = new Date(date).toISOString();
        return isoString.slice(0, 10);
    };

    const fetchMeetingDetails = async (date) => {
        try {
            const formattedDate = formatDateToAPI(date);
            const response = await apiService.get(
                `${API_ENDPOINTS.GET_chapter_meetingsMom}?chapter_id=${JSON.parse(localStorage.getItem("logged_data"))?.user.chapter_id}&date=${formattedDate}`
            );

            if (response?.success && response?.data) {
                setMeetingDetails(response.data);
                setChapterMeetingId(response.data.id);
                setMom(response.data.mom || '');
                setStartTime(response.data.start_time?.substring(0, 5) || '10:00');
                setEndTime(response.data.end_time?.substring(0, 5) || '12:00');
                
                // Call fetchMembers with the chapter_meeting_id if meeting exists
                fetchMembers(response.data.id);
            } else {
                setMeetingDetails(null);
                setChapterMeetingId(null);
                setMom('');
                setStartTime('10:00');
                setEndTime('12:00');
                setMembers([]); // Clear members if no meeting exists
            }
        } catch (error) {
            console.error('Error fetching meeting details:', error);
            toast.error('Failed to fetch meeting details');
            setMembers([]); // Clear members on error
        }
    };

    const handleMeetingSubmit = async () => {
        if (!mom.trim()) {
            toast.error('Minutes of Meeting is required');
            return;
        }

        try {
            setIsLoading(true);
            const formattedDate = formatDateToAPI(selectedDate);

            const meetingPayload = {
                chapter_id: `${JSON.parse(localStorage.getItem("logged_data"))?.user.chapter_id}`,
                date: formattedDate,
                mom: mom,
                start_time: `${startTime}:00`,
                end_time: `${endTime}:00`,
            };

            if (meetingDetails?.id) {
                meetingPayload.id = meetingDetails.id;
            }

            const response = await apiService.post(
                API_ENDPOINTS.POST_meeting_add_or_update,
                meetingPayload
            );

            if (response?.success) {
                toast.success('Meeting details saved successfully');
                setMeetingDetails(response.data);
                setChapterMeetingId(response.data.id);
                fetchMembers(response.data.id);
            } else {
                toast.error('Failed to save meeting details');
            }
        } catch (error) {
            console.error('Error saving meeting details:', error);
            toast.error('Failed to save meeting details');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMembers = useCallback(async (meetingId) => {
        if (!meetingId) return;
        
        try {
            const response = await apiService.get(
                `${API_ENDPOINTS.GET_member_by_chapters}?chapter_id=${JSON.parse(localStorage.getItem("logged_data"))?.user.chapter_id}&chapter_meeting_id=${meetingId}`
            );

            if (response?.success && response?.data) {
                setMembers(response.data);
            }
        } catch (error) {
            console.error("API error:", error);
            setMembers([]);
        }
    }, []);

    useEffect(() => {
        if (selectedDate) {
            fetchMeetingDetails(selectedDate);
        }
    }, [selectedDate]);

    return (
        <Box pt={2} pb={4}>
            {/* Header Section with Date Picker */}
            <Card sx={{ p: 2, mb: 3 }}>
                <FlexBetween>
                    <FlexBox alignItems="center" gap={2}>
                        <Typography variant="h6" color="primary">
                            Select Meeting Date
                        </Typography>
                        <DatePickerWrapper>
                            <DatePicker
                                selected={selectedDate}
                                onChange={date => setSelectedDate(date)}
                                placeholderText="Select Date"
                                dateFormat="MM/dd/yyyy"
                            />
                        </DatePickerWrapper>
                    </FlexBox>
                </FlexBetween>
            </Card>

            {/* Meeting Details Form */}
            <Card sx={{ p: 3, mb: 3 }}>
                <FlexBetween mb={3}>
                    <Typography variant="h5" color="primary" fontWeight="600">
                        {meetingDetails ? 'Update Meeting Details' : 'Add Meeting Details'}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleMeetingSubmit}
                        disabled={isLoading}
                        sx={{ 
                            minWidth: '150px',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '1rem'
                        }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : meetingDetails ? 'Update Meeting' : 'Save Meeting'}
                    </Button>
                </FlexBetween>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                        fullWidth
                        label="Minutes of Meeting"
                        multiline
                        rows={3}
                        value={mom}
                        onChange={(e) => setMom(e.target.value)}
                        required
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            type="time"
                            label="Start Time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                            fullWidth
                        />
                        <TextField
                            type="time"
                            label="End Time"
                            value={endTime}
                            onChange={(e) => {
                                const newEndTime = e.target.value;
                                // Validate that End Time is greater than Start Time
                                if (newEndTime <= startTime) {
                                    toast.error('End Time must be greater than Start Time');
                                } else {
                                    setEndTime(newEndTime);
                                }
                            }}
                            InputLabelProps={{ shrink: true }}
                            required
                            fullWidth
                        />
                    </Box>
                </Box>
            </Card>

            {/* Members Table Section */}
            {meetingDetails && chapterMeetingId && (
                <Card sx={{ p: 3 }}>
                    <FlexBox mb={3}>
                        <SearchInput
                            bordered={false}
                            placeholder="Search Member"
                            onChange={e => setSearchValue(e.target.value)}
                            value={searchValue}
                        />
                    </FlexBox>

                    <MembersTable
                        members={members}
                        searchQuery={searchValue}
                        selectedDate={selectedDate}
                        onAttendanceMarked={() => fetchMembers(chapterMeetingId)}
                        chapterId={`${JSON.parse(localStorage.getItem("logged_data"))?.user.chapter_id}`}
                        meetingDetails={meetingDetails}
                        chapterMeetingId={chapterMeetingId} 
                        startTime={startTime}
                    />
                </Card>
            )}
        </Box>
    );
};

export default ChapterAttendanceMembers;